import React from 'react'
import { useSelector } from 'react-redux'

import { RootStateTypeExtra } from '@store/types'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'

import { useCompareProperty } from '@adUtilities/components/compare-property/utils'
import {
  LotProperty,
  PropertyCompare,
  PropertyType,
  UnitProperty,
} from '@adUtilities/types'

interface UsePropertyCompareFirebaseTriggerArgs {
  comparePropertyProps?: ReturnType<typeof useCompareProperty>
}

type Property = UnitProperty | LotProperty

const usePropertyCompareFirebaseTrigger = (
  args?: UsePropertyCompareFirebaseTriggerArgs
) => {
  const { comparePropertyProps } = args || {}
  const [isProcessing, setIsProcessing] = React.useState(false)
  const propertyCompareSession = useSelector(
    (state: RootStateTypeExtra) => state.firestore.session?.propertyCompare
  )
  const projectIdentity = useSelector(
    (state: RootStateTypeExtra) => state.projectIdentity
  )

  const {
    validateAdd,
    validateRemove,
    validateReplace,
    hasEmptyComparableSlot,
  } = comparePropertyProps || {}

  const isActive = !!propertyCompareSession?.active

  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const triggerComparisonStart = React.useCallback(
    async (args: {
      property: Property
      index?: number
      preventDuplicate?: boolean
      replaceLast?: boolean
    }) => {
      if (!validateAdd) {
        return
      }
      if (isProcessing) {
        return
      }
      const { validated, data: validatedData } = validateAdd(args)

      if (!(validated && validatedData)) {
        return
      }

      const payload: PropertyCompare = {
        active: true,
        properties: [],
      }

      if (
        validatedData.property.propertyType === PropertyType.Unit &&
        args.property.propertyType === PropertyType.Unit
      ) {
        payload.properties.push({
          index: validatedData.index,
          propertyType: validatedData.property.propertyType,
          propertyId: validatedData.property.data.name,
          references: args.property.references,
        })
      }

      if (
        validatedData.property.propertyType === PropertyType.Lot &&
        args.property.propertyType === PropertyType.Lot
      ) {
        payload.properties.push({
          index: validatedData.index,
          propertyType: validatedData.property.propertyType,
          propertyId: validatedData.property.data.name,
          references: args.property.references,
        })
      }

      setIsProcessing(true)
      try {
        await firebaseControlQuery.update({
          [`propertyCompare`]: payload,
        })
      } catch (error) {
        console.log(error)
      } finally {
        setIsProcessing(false)
      }
    },
    [validateAdd, isProcessing]
  )

  const triggerComparisonStop = React.useCallback(async () => {
    if (isProcessing) {
      return
    }

    const payload: PropertyCompare = {
      active: false,
      properties: [],
    }
    setIsProcessing(true)
    try {
      await firebaseControlQuery.update({
        [`propertyCompare`]: payload,
      })
    } catch (error) {
      console.log(error)
    } finally {
      setIsProcessing(false)
    }
  }, [isProcessing])

  const triggerComparisonReplace = React.useCallback(
    async (args: {
      property: Property
      index: number
      preventDuplicate?: boolean
    }) => {
      if (!validateReplace) {
        return
      }
      if (isProcessing) {
        return
      }

      const { validated, data: validatedData } = validateReplace(args)

      if (!(validated && validatedData)) {
        return
      }

      const payload: PropertyCompare = {
        active: !!propertyCompareSession?.active,
        properties: [...(propertyCompareSession?.properties || [])],
      }

      const matchedIndex = payload.properties.findIndex(
        (item) => item.index === args.index
      )

      if (matchedIndex < 0) {
        return
      }

      const matchedProperty = payload.properties[matchedIndex]

      if (
        matchedProperty.propertyType === PropertyType.Unit &&
        args.property.propertyType === PropertyType.Unit
      ) {
        payload.properties[matchedIndex] = {
          index: args.index,
          propertyId: args.property.data.name,
          propertyType: args.property.propertyType,
          references: args.property.references,
        }
      }

      if (
        matchedProperty.propertyType === PropertyType.Lot &&
        args.property.propertyType === PropertyType.Lot
      ) {
        payload.properties[matchedIndex] = {
          index: args.index,
          propertyId: args.property.data.name,
          propertyType: args.property.propertyType,
          references: args.property.references,
        }
      }

      setIsProcessing(true)
      try {
        await firebaseControlQuery.update({
          [`propertyCompare`]: payload,
        })
      } catch (error) {
        console.log(error)
      } finally {
        setIsProcessing(false)
      }
    },
    [validateReplace, isProcessing, propertyCompareSession]
  )

  const triggerComparisonAdd = React.useCallback(
    async (args: {
      property: Property
      index?: number
      preventDuplicate?: boolean
      replaceLast?: boolean
    }) => {
      if (!validateAdd) {
        return
      }
      if (isProcessing) {
        return
      }

      const { validated, data: validatedData } = validateAdd(args)

      if (!(validated && validatedData)) {
        return
      }

      if (!hasEmptyComparableSlot) {
        triggerComparisonReplace({
          index: validatedData.index,
          preventDuplicate: args.preventDuplicate,
          property: validatedData.property,
        })
        return
      }

      const payload: PropertyCompare = {
        active: !!propertyCompareSession?.active,
        properties: [...(propertyCompareSession?.properties || [])],
      }

      if (
        validatedData.property.propertyType === PropertyType.Unit &&
        args.property.propertyType === PropertyType.Unit
      ) {
        payload.properties.push({
          index: validatedData.index,
          propertyType: validatedData.property.propertyType,
          propertyId: validatedData.property.data.name,
          references: args.property.references,
        })
      }

      if (
        validatedData.property.propertyType === PropertyType.Lot &&
        args.property.propertyType === PropertyType.Lot
      ) {
        payload.properties.push({
          index: validatedData.index,
          propertyType: validatedData.property.propertyType,
          propertyId: validatedData.property.data.name,
          references: args.property.references,
        })
      }

      setIsProcessing(true)
      try {
        await firebaseControlQuery.update({
          [`propertyCompare`]: payload,
        })
      } catch (error) {
        console.log(error)
      } finally {
        setIsProcessing(false)
      }
    },
    [
      validateAdd,
      isProcessing,
      propertyCompareSession,
      hasEmptyComparableSlot,
      triggerComparisonReplace,
    ]
  )

  const triggerComparisonRemove = React.useCallback(
    async (args: { index: number; keepLast?: boolean }) => {
      const { index } = args
      if (!validateRemove) {
        return
      }
      if (isProcessing) {
        return
      }

      const { validated } = validateRemove(args)

      if (!validated) {
        return
      }

      const payload: PropertyCompare = {
        active: !!propertyCompareSession?.active,
        properties: [...(propertyCompareSession?.properties || [])],
      }
      payload.properties = payload.properties.filter(
        (item) => item.index !== index
      )
      setIsProcessing(true)
      try {
        await firebaseControlQuery.update({
          [`propertyCompare`]: payload,
        })
      } catch (error) {
        console.log(error)
      } finally {
        setIsProcessing(false)
      }
    },
    [validateRemove, isProcessing, propertyCompareSession]
  )

  return React.useMemo(
    () => ({
      isProcessing,
      triggerComparisonStart,
      triggerComparisonStop,
      triggerComparisonAdd,
      triggerComparisonReplace,
      triggerComparisonRemove,
      isActive,
    }),
    [
      isProcessing,
      triggerComparisonStart,
      triggerComparisonStop,
      triggerComparisonAdd,
      triggerComparisonReplace,
      triggerComparisonRemove,
      isActive,
    ]
  )
}

export default usePropertyCompareFirebaseTrigger
